/* eslint-disable react-hooks/immutability */
import { useCallback, useEffect, useRef } from "react";

export interface UseWebSocketOptions<T = any> {
  url: string;
  token?: string;
  getToken?: () => string;
  onMessage?: (data: T, event: MessageEvent<string>) => void;
  onOpen?: (event?: Event) => void;
  onClose?: (event?: CloseEvent) => void;
  onError?: (event?: Event) => void;
  onDisable?: () => void;
}

const isDisable = import.meta.env.VITE_APP_DISABLE_WSS === "true";
const HEARTBEAT_INTERVAL = 25000;
const HEARTBEAT_TIMEOUT = 45000;
const MAX_RECONNECT_ATTEMPTS = 10;
const MAX_RECONNECT_DELAY = 30000;
const WAIT_INITIAL_DELAY = 100;
const WAIT_MAX_DELAY = 500;

export function useWebSocket<T = any>(options: UseWebSocketOptions<T>) {
  const { url, token, getToken, onMessage, onOpen, onClose, onError, onDisable } =
    options;

  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);
  const onDisableRef = useRef(onDisable);
  const isExplicitClose = useRef(false);
  const socketRef = useRef<WebSocket | null>(null);
  const intervalRef = useRef<number | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const heartbeatTimeoutRef = useRef<number | null>(null);
  const lastPingTimeRef = useRef<number>(0);
  const retryCount = useRef(0);

  useEffect(() => {
    onMessageRef.current = onMessage;
    onOpenRef.current = onOpen;
    onCloseRef.current = onClose;
    onErrorRef.current = onError;
    onDisableRef.current = onDisable;
  }, [onMessage, onOpen, onClose, onError, onDisable]);

  const cleanup = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (retryTimeoutRef.current !== null) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (heartbeatTimeoutRef.current !== null) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  const resolveToken = useCallback(() => getToken?.() || token || "", [getToken, token]);

  const getStableTabId = useCallback(() => {
    const existingTabId = window.sessionStorage.getItem("tabId");
    if (existingTabId) return existingTabId;

    const tabId = `tmatab_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    window.sessionStorage.setItem("tabId", tabId);
    return tabId;
  }, []);

  const connect = useCallback(() => {
    const currentToken = resolveToken();

    if (!currentToken || !url) {
      return;
    }
    if (isDisable) {
      onDisableRef.current?.();
      return;
    }

    if (
      socketRef.current?.readyState === WebSocket.OPEN ||
      socketRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    isExplicitClose.current = false;
    cleanup();

    const wsUrl = `${url}?token=${encodeURIComponent(currentToken)}`;
    getStableTabId();

    console.log(`WebSocket connecting (attempt ${retryCount.current + 1})`);

    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onopen = (event: Event) => {
      if (socketRef.current !== socket) return;

      console.log("WebSocket Connected successfully");
      retryCount.current = 0;
      onOpenRef.current?.(event);

      intervalRef.current = window.setInterval(() => {
        if (socket.readyState !== WebSocket.OPEN) return;

        const pingTime = Date.now();
        lastPingTimeRef.current = pingTime;
        socket.send(
          JSON.stringify({
            action: "heartbeat",
            message: "PING",
            timestamp: pingTime,
          })
        );

        if (heartbeatTimeoutRef.current) {
          clearTimeout(heartbeatTimeoutRef.current);
        }

        heartbeatTimeoutRef.current = window.setTimeout(() => {
          console.warn("Heartbeat timeout - closing connection");
          socket.close(3001, "Heartbeat timeout");
        }, HEARTBEAT_TIMEOUT);
      }, HEARTBEAT_INTERVAL);
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      if (socketRef.current !== socket) return;

      try {
        const data: T = JSON.parse(event.data);
        if (
          data &&
          typeof data === "object" &&
          (("action" in data && (data as any).action === "heartbeat") ||
            ("response" in data && (data as any).response === "OK"))
        ) {
          if (heartbeatTimeoutRef.current) {
            clearTimeout(heartbeatTimeoutRef.current);
            heartbeatTimeoutRef.current = null;
          }
          return;
        }
        onMessageRef.current?.(data, event);
      } catch (err) {
        console.error("Parse error", err);
      }
    };

    const scheduleReconnect = (reason: string, code?: number) => {
      if (isExplicitClose.current) {
        console.log("Explicit close - cancelling reconnect", reason, code);
        return;
      }

      cleanup();
      if (retryCount.current >= MAX_RECONNECT_ATTEMPTS) {
        console.error("Max WebSocket reconnect attempts reached");
        onErrorRef.current?.(new Event("max_retries_exceeded"));
        return;
      }

      retryCount.current += 1;
      const backoffTime = Math.min(
        1000 * 2 ** retryCount.current,
        MAX_RECONNECT_DELAY
      );

      console.log(`${reason}. Reconnecting in ${backoffTime / 1000}s...`);

      retryTimeoutRef.current = window.setTimeout(() => {
        retryTimeoutRef.current = null;
        connect();
      }, backoffTime);
    };

    socket.onclose = (event: CloseEvent) => {
      if (socketRef.current !== socket) {
        return;
      }

      if (isExplicitClose.current) {
        return;
      }

      console.log(`WebSocket closed: ${event.code}`);
      onCloseRef.current?.(event);

      if (event.code !== 1000) {
        scheduleReconnect("Connection lost", event.code);
      }
    };

    socket.onerror = (event: Event) => {
      if (socketRef.current !== socket) return;

      console.error("WebSocket error observed:", event);
      onErrorRef.current?.(event);
    };
  }, [url, resolveToken, cleanup, getStableTabId]);

  const isConnected = useCallback(
    () => socketRef.current?.readyState === WebSocket.OPEN,
    []
  );

  const send = useCallback((data: any) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  const close = useCallback(() => {
    isExplicitClose.current = true;
    cleanup();
    socketRef.current?.close(1000, "Manual close");
    socketRef.current = null;
  }, [cleanup]);

  const reconnect = useCallback(() => {
    isExplicitClose.current = true;
    cleanup();

    if (socketRef.current) {
      socketRef.current.close(1000, "Manual reconnect");
      socketRef.current = null;
    }

    retryTimeoutRef.current = window.setTimeout(() => {
      retryTimeoutRef.current = null;
      isExplicitClose.current = false;
      retryCount.current = 0;
      connect();
    }, 100);
  }, [cleanup, connect]);

  const waitUntilConnected = useCallback(
    (timeoutMs = 10000) =>
      new Promise<boolean>((resolve) => {
        if (isConnected()) {
          resolve(true);
          return;
        }

        const startedAt = Date.now();
        let delay = WAIT_INITIAL_DELAY;

        const checkConnection = () => {
          if (isConnected()) {
            resolve(true);
            return;
          }

          if (Date.now() - startedAt >= timeoutMs) {
            resolve(false);
            return;
          }

          window.setTimeout(checkConnection, delay);
          delay = Math.min(Math.ceil(delay * 1.5), WAIT_MAX_DELAY);
        };

        window.setTimeout(checkConnection, delay);
      }),
    [isConnected]
  );

  useEffect(() => {
    connect();

    return () => {
      isExplicitClose.current = true;
      cleanup();

      if (socketRef.current) {
        socketRef.current.close(1000, "Component unmounting");
        socketRef.current = null;
      }
    };
  }, [connect, cleanup]);

  useEffect(() => {
    const recoverIfNeeded = () => {
      const state = socketRef.current?.readyState;
      if (state !== WebSocket.OPEN && state !== WebSocket.CONNECTING) {
        reconnect();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        recoverIfNeeded();
      }
    };

    window.addEventListener("online", recoverIfNeeded);
    window.addEventListener("focus", recoverIfNeeded);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("online", recoverIfNeeded);
      window.removeEventListener("focus", recoverIfNeeded);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [reconnect]);

  return {
    send,
    close,
    reconnect,
    isConnected,
    waitUntilConnected,
  };
}
